import { REPORT_MAX_DAYS, type ReportSummary, reportDays } from "@luckymenu/core";

/**
 * Reports screens (P11-T05, P11-T06): the range picker's presets, the bar
 * chart's bars, and sorting for the detail tables. Pure, so both staff apps
 * share them. Dates are business dates (YYYY-MM-DD) of the restaurant.
 */

export const RANGE_PRESETS = [
	"today",
	"yesterday",
	"last7",
	"last30",
	"thisMonth",
	"lastMonth",
	"custom",
] as const;
export type RangePreset = (typeof RANGE_PRESETS)[number];

export type ReportRange = { from: string; to: string };

/** "2026-09-30" plus `days` (negative goes back). Pure calendar arithmetic, in UTC. */
export function addDays(date: string, days: number): string {
	const ms = Date.parse(`${date}T00:00:00Z`) + days * 86_400_000;
	return new Date(ms).toISOString().slice(0, 10);
}

/** The first day of the month of `date`, months later (negative goes back). */
function monthStart(date: string, months = 0): string {
	const [y, m] = date.split("-").map(Number) as [number, number];
	const index = y * 12 + (m - 1) + months;
	const year = Math.floor(index / 12);
	return `${year}-${String((index % 12) + 1).padStart(2, "0")}-01`;
}

/** The business dates a preset covers, with `today` the restaurant's business date. */
export function presetRange(preset: Exclude<RangePreset, "custom">, today: string): ReportRange {
	switch (preset) {
		case "today":
			return { from: today, to: today };
		case "yesterday": {
			const day = addDays(today, -1);
			return { from: day, to: day };
		}
		case "last7":
			return { from: addDays(today, -6), to: today };
		case "last30":
			return { from: addDays(today, -29), to: today };
		case "thisMonth":
			return { from: monthStart(today), to: today };
		case "lastMonth":
			return { from: monthStart(today, -1), to: addDays(monthStart(today), -1) };
	}
}

const DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * What is wrong with a custom range, as the server would refuse it: a date
 * missing, the start after the end, or more than 366 days. Null when fine.
 */
export function rangeProblem(range: ReportRange): "missing" | "order" | "tooLong" | null {
	if (!DATE.test(range.from) || !DATE.test(range.to)) return "missing";
	if (range.from > range.to) return "order";
	if (reportDays(range.from, range.to) > REPORT_MAX_DAYS) return "tooLong";
	return null;
}

/** One bar of the sales chart: a business date, or a month for long ranges. */
export type SalesBar = {
	/** YYYY-MM-DD for a day, YYYY-MM for a month. */
	key: string;
	kind: "day" | "month";
	bills: number;
	totalPaise: number;
};

/** Ranges up to this many days get a bar per day; longer ones a bar per month. */
export const DAILY_BARS_MAX_DAYS = 31;

/**
 * The bars of the sales chart for a range: every day of the range (days
 * without bills as zero) up to 31 days, else every month. `max` is the
 * tallest bar's total, for scaling.
 */
export function salesBars(
	days: ReportSummary["days"],
	range: ReportRange,
): { bars: SalesBar[]; max: number } {
	const byDay = new Map(days.map((d) => [d.businessDate, d]));
	const bars: SalesBar[] = [];
	if (reportDays(range.from, range.to) <= DAILY_BARS_MAX_DAYS) {
		for (let day = range.from; day <= range.to; day = addDays(day, 1)) {
			const found = byDay.get(day);
			bars.push({
				key: day,
				kind: "day",
				bills: found?.bills ?? 0,
				totalPaise: found?.totalPaise ?? 0,
			});
		}
	} else {
		const months = new Map<string, SalesBar>();
		for (let m = monthStart(range.from); m <= range.to; m = monthStart(m, 1)) {
			const key = m.slice(0, 7);
			months.set(key, { key, kind: "month", bills: 0, totalPaise: 0 });
		}
		for (const d of days) {
			const bar = months.get(d.businessDate.slice(0, 7));
			if (!bar) continue;
			bar.bills += d.bills;
			bar.totalPaise += d.totalPaise;
		}
		bars.push(...months.values());
	}
	return { bars, max: bars.reduce((m, b) => Math.max(m, b.totalPaise), 0) };
}

/** "2026-09-30" as "30/09", "2026-09" as "09/26": short labels under the bars. */
export function barLabel(bar: Pick<SalesBar, "key" | "kind">): string {
	if (bar.kind === "month") {
		const [y, m] = bar.key.split("-");
		return `${m}/${y?.slice(2)}`;
	}
	const [, m, d] = bar.key.split("-");
	return `${d}/${m}`;
}

export type SortDirection = "asc" | "desc";
export type SortState<K extends string> = { key: K; direction: SortDirection };

/**
 * Sorted copy of `rows` by one column: numbers by value, text by the
 * locale's order (nulls last either way), ties keeping their order.
 */
export function sortRows<T, K extends string>(
	rows: readonly T[],
	sort: SortState<K>,
	value: (row: T, key: K) => string | number | null,
): T[] {
	const sign = sort.direction === "asc" ? 1 : -1;
	return rows
		.map((row, index) => ({ row, index, v: value(row, sort.key) }))
		.sort((a, b) => {
			if (a.v === null || b.v === null) {
				return a.v === b.v ? a.index - b.index : a.v === null ? 1 : -1;
			}
			const order =
				typeof a.v === "number" && typeof b.v === "number"
					? a.v - b.v
					: String(a.v).localeCompare(String(b.v));
			return order === 0 ? a.index - b.index : sign * order;
		})
		.map((e) => e.row);
}

/**
 * The sort after a column header is chosen: the same column flips direction;
 * another starts with its natural direction (text A to Z, numbers highest first).
 */
export function nextSort<K extends string>(
	current: SortState<K>,
	key: K,
	natural: SortDirection,
): SortState<K> {
	if (current.key === key) {
		return { key, direction: current.direction === "asc" ? "desc" : "asc" };
	}
	return { key, direction: natural };
}

/** Basis points of a part in a whole, for shares such as "32% of sales"; 0 for an empty whole. */
export function shareBp(part: number, whole: number): number {
	return whole <= 0 ? 0 : Math.round((part * 10_000) / whole);
}
